/**
 * Shared document PDF generator using browser print.
 * Produces professional, well-paginated print output for all three document types.
 */

import { CURRENT_DATE } from './constants';

const BRAND_PURPLE = '#7b1fa2';
const BRAND_PURPLE_DARK = '#6B2D8B';
const BRAND_PURPLE_LIGHT = '#F5F0FF';

const escHtml = t => String(t ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

const fmtDate = d => {
  if (!d || d === 'Unknown' || d === 'N/A') return null;
  try { return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }); }
  catch { return String(d); }
};

const fmtWinProb = v => {
  if (!v || v === 'Unknown' || v === 'N/A') return null;
  return `${String(v).replace(/%$/, '')}% likely to close`;
};

const scoreColor = s => s === 'green' ? '#2E7D32' : s === 'yellow' ? '#E65100' : s === 'red' ? '#C62828' : '#555';
const scoreBg = s => s === 'green' ? '#E8F5E9' : s === 'yellow' ? '#FFF3E0' : s === 'red' ? '#FFEBEE' : '#F5F5F5';

const bulletList = (items, color = '#555', symbol = '·') => {
  if (!items?.length) return '';
  return `<ul style="margin:4px 0 10px 0;padding:0;list-style:none">${
    items.map(i => `<li style="display:flex;gap:8px;margin:4px 0;font-size:13px"><span style="color:${color};flex-shrink:0;margin-top:1px">${symbol}</span><span>${escHtml(i)}</span></li>`).join('')
  }</ul>`;
};

const sharedStyles = `
  @page { size: A4; margin: 2cm 2.5cm; }
  * { box-sizing: border-box; }
  body { font-family: -apple-system, 'Segoe UI', Arial, sans-serif; color: #111; background: white; font-size: 13px; line-height: 1.6; margin: 0; }
  h1 { font-size: 22px; font-weight: 700; margin: 0 0 4px 0; color: #111; }
  h2 { font-size: 15px; font-weight: 700; color: #333; border-bottom: 2px solid ${BRAND_PURPLE_DARK}; padding-bottom: 6px; margin: 0 0 12px 0; text-transform: uppercase; letter-spacing: .5px; page-break-after: avoid; }
  h3 { font-size: 14px; font-weight: 700; color: ${BRAND_PURPLE_DARK}; margin: 0 0 8px 0; page-break-after: avoid; }
  p { margin: 0 0 10px 0; }
  .page-header { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 12px; padding-bottom: 10px; border-bottom: 3px solid ${BRAND_PURPLE}; }
  .doc-label { font-size: 11px; font-weight: 700; color: ${BRAND_PURPLE}; letter-spacing: 1.5px; text-transform: uppercase; margin-bottom: 4px; }
  .doc-meta { font-size: 12px; color: #555; margin-top: 4px; }
  .doc-date { text-align: right; font-size: 11px; color: #888; white-space: nowrap; }
  .section { margin-bottom: 24px; page-break-inside: avoid; }
  .highlight-box { padding: 14px 16px; border-left: 4px solid ${BRAND_PURPLE}; background: ${BRAND_PURPLE_LIGHT}; border-radius: 0 8px 8px 0; font-size: 14px; line-height: 1.7; margin-bottom: 12px; }
  .card { padding: 12px 14px; border: 1px solid #E0E0E0; border-radius: 8px; margin-bottom: 10px; page-break-inside: avoid; }
  .card-accent { border-left: 3px solid ${BRAND_PURPLE}; }
  .footer { margin-top: 32px; padding-top: 12px; border-top: 1px solid #E0E0E0; font-size: 11px; color: #999; text-align: center; }
  .flag-red { color: #C62828; } .flag-yellow { color: #E65100; } .flag-green { color: #2E7D32; }
  @media print { body { -webkit-print-color-adjust: exact; print-color-adjust: exact; } .no-print { display: none; } }
`;

function openPrintWindow(title, html) {
  const fullHtml = `<!DOCTYPE html><html lang="en"><head><meta charset="utf-8"><title>${escHtml(title)}</title><style>${sharedStyles}</style></head><body>${html}</body></html>`;
  const win = window.open('', '_blank');
  if (!win) return;
  win.document.write(fullHtml);
  win.document.close();
  win.focus();
  setTimeout(() => win.print(), 400);
}

// ─── Detailed Analysis ──────────────────────────────────────────────────────

export function generateDetailedAnalysisPDF(analysisInput, staffingData, teamAnalysisData) {
  let parsed = null;
  try {
    let jsonText = typeof analysisInput === 'string' ? analysisInput : JSON.stringify(analysisInput);
    const fence = jsonText.match(/```(?:json)?\s*([\s\S]*?)\s*```/);
    if (fence) jsonText = fence[1];
    parsed = JSON.parse(jsonText);
  } catch (e) { return; }
  if (!parsed?.summary) return;

  const teamScores = teamAnalysisData?.scores;
  const teamMembers = teamAnalysisData?.executiveSummary?.team || [];

  const opp = staffingData || {};
  const projectName = opp.projectName || opp.name || opp.opportunity_name || 'Team Analysis';
  const clientName = opp.clientName || opp.client_name || opp.client || '';
  const closeDate = fmtDate(opp.estimatedCloseDate || opp.close_date);
  const startDate = fmtDate(opp.start_date || opp.startDate);
  const winProbStr = fmtWinProb(opp.winProbability || opp.win_probability);
  const dateStr = CURRENT_DATE.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });

  const summary = parsed.summary || {};
  const scorecard = parsed.scorecard || [];
  const sections = parsed.detailed_sections || [];
  const tradeoffs = parsed.key_tradeoffs || [];
  const actions = parsed.mitigation_actions || [];
  const flags = parsed.flags || {};

  const metaLine = [closeDate ? `Close: ${closeDate}` : '', startDate ? `Start: ${startDate}` : '', winProbStr || ''].filter(Boolean).join('  ·  ');
  const memberLine = teamMembers.map(m => `${escHtml(m.name)} (${escHtml(m.role)})`).join('  ·  ');

  const dimensionKeyMap = { 'Technical': 'technical', 'Chemistry': 'chemistry', 'Financial': 'financial', 'Availability': 'availability', 'Experience': 'experience' };
  const scorecardHtml = `
    <table style="width:100%;border-collapse:collapse;font-size:13px">
      <thead>
        <tr style="border-bottom:2px solid #E0E0E0">
          <th style="text-align:left;padding:7px 10px;color:#666;font-weight:600;font-size:11px;text-transform:uppercase;letter-spacing:.5px;width:120px">Dimension</th>
          <th style="text-align:center;padding:7px 10px;color:#666;font-weight:600;font-size:11px;text-transform:uppercase;letter-spacing:.5px;width:70px">Score</th>
          <th style="text-align:left;padding:7px 10px;color:#666;font-weight:600;font-size:11px;text-transform:uppercase;letter-spacing:.5px">Assessment</th>
        </tr>
      </thead>
      <tbody>
        ${scorecard.map(c => {
          const key = dimensionKeyMap[c.dimension] || c.dimension.toLowerCase();
          const rawScore = teamScores?.[key] ?? c.score ?? null;
          const score = typeof rawScore === 'number' ? rawScore : (rawScore != null ? parseFloat(rawScore) : null);
          const status = score != null ? (score >= 7 ? 'green' : score >= 5 ? 'yellow' : 'red') : (c.status || 'neutral');
          const numColor = scoreColor(status);
          const scoreStr = score != null ? `${score.toFixed(1)}<span style="font-size:10px;font-weight:400;color:#888">/10</span>` : '—';
          return `<tr style="border-bottom:1px solid #F0F0F0">
            <td style="padding:8px 10px;font-weight:600;color:#222">${escHtml(c.dimension)}</td>
            <td style="text-align:center;padding:8px 10px;font-size:15px;font-weight:700;color:${numColor};white-space:nowrap">${scoreStr}</td>
            <td style="padding:8px 10px;color:#444;font-size:12px">${escHtml(c.summary || '')}</td>
          </tr>`;
        }).join('')}
      </tbody>
    </table>`;

  const flagsHtml = (flags.red?.length || flags.yellow?.length || flags.green?.length) ? `
    <div class="section">
      <h2>Flags</h2>
      ${flags.green?.length ? bulletList(flags.green, '#2E7D32', '●') : ''}
      ${flags.yellow?.length ? bulletList(flags.yellow, '#E65100', '▲') : ''}
      ${flags.red?.length ? bulletList(flags.red, '#C62828', '✕') : ''}
    </div>` : '';

  const sectionsHtml = sections.map(sec =>
    `<div class="section" style="border-left:3px solid ${BRAND_PURPLE_DARK};padding-left:14px">
      <h3>${escHtml(sec.title)}</h3>
      ${sec.content ? `<p style="color:#333">${escHtml(sec.content)}</p>` : ''}
      ${bulletList(sec.strengths, '#2E7D32', '●')}
      ${bulletList(sec.concerns, '#E65100', '▲')}
      ${bulletList(sec.recommendations, BRAND_PURPLE, '→')}
      ${bulletList(sec.keyPoints, '#555', '·')}
    </div>`
  ).join('');

  const tradeoffsHtml = tradeoffs.length ? `
    <div class="section">
      <h2>Key Tradeoffs</h2>
      ${tradeoffs.map(t => `
        <div style="display:flex;gap:10px;align-items:flex-start;margin-bottom:8px;padding:10px;background:#FFF8E1;border-radius:6px;border-left:3px solid #E65100;page-break-inside:avoid">
          <span style="font-size:10px;font-weight:700;padding:2px 8px;border-radius:10px;background:${t.severity === 'high' ? '#C62828' : t.severity === 'medium' ? '#E65100' : '#555'};color:white;white-space:nowrap;flex-shrink:0;margin-top:2px">${escHtml((t.severity || '').toUpperCase())}</span>
          <span style="font-size:13px;color:#333">${escHtml(t.tradeoff)}</span>
        </div>`).join('')}
    </div>` : '';

  const actionsHtml = actions.length ? `
    <div style="page-break-before:always">
      <h2>Mitigation Actions</h2>
      ${actions.map(a => `
        <div class="card card-accent" style="page-break-inside:avoid">
          <div style="margin-bottom:6px"><strong style="color:#C62828">Risk:</strong> <span>${escHtml(a.risk)}</span></div>
          <div style="margin-bottom:6px"><strong style="color:#2E7D32">Action:</strong> <span>${escHtml(a.action)}</span></div>
          <div style="display:flex;gap:24px;font-size:12px;color:#555;flex-wrap:wrap">
            ${a.owner ? `<span><strong>Owner:</strong> ${escHtml(a.owner)}</span>` : ''}
            ${a.timing ? `<span><strong>Timing:</strong> ${escHtml(a.timing)}</span>` : ''}
            ${a.success_indicator ? `<span><strong>Success:</strong> ${escHtml(a.success_indicator)}</span>` : ''}
          </div>
        </div>`).join('')}
    </div>` : '';

  const body = `
    <div class="page-header">
      <div>
        <div class="doc-label">Concierge · Team Analysis</div>
        <h1>${clientName ? `${escHtml(clientName)} — ` : ''}${escHtml(projectName)}</h1>
        ${metaLine ? `<div class="doc-meta">${escHtml(metaLine)}</div>` : ''}
        ${memberLine ? `<div style="font-size:11px;color:#666;margin-top:3px">${memberLine}</div>` : ''}
      </div>
      <div class="doc-date">Generated ${escHtml(dateStr)}</div>
    </div>

    ${summary.overall_assessment ? `
      <div class="section">
        <h2>Overall Assessment</h2>
        <div class="highlight-box">${escHtml(summary.overall_assessment)}</div>
        ${summary.key_strengths?.length ? `<div style="margin-top:8px">${bulletList(summary.key_strengths, '#2E7D32', '●')}</div>` : ''}
        ${summary.primary_risks?.length ? bulletList(summary.primary_risks, '#E65100', '▲') : ''}
        ${summary.top_recommendations?.length ? bulletList(summary.top_recommendations, BRAND_PURPLE, '→') : ''}
      </div>` : ''}

    ${scorecard.length ? `
      <div class="section">
        <h2>Score Summary</h2>
        ${scorecardHtml}
      </div>` : ''}

    ${flagsHtml}

    ${sections.length ? `
      <div style="page-break-before:always">
        <h2>Detailed Analysis</h2>
        ${sectionsHtml}
      </div>` : ''}

    ${tradeoffsHtml}
    ${actionsHtml}

    <div class="footer">Generated by Concierge · ${escHtml(dateStr)} · Confidential</div>
  `;

  openPrintWindow(`${projectName} — Team Analysis`, body);
}

// ─── Client Introduction ────────────────────────────────────────────────────

export function generateClientIntroductionPDF(document, projectContext, clientContext) {
  if (!document?.content) return;

  const content = document.content;
  const dateStr = new Date(document.generated_date || CURRENT_DATE).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  const projectName = projectContext?.project_name || '';
  const clientName = clientContext?.client_name || projectContext?.client_name || '';
  const closeDate = fmtDate(projectContext?.close_date);
  const startDate = fmtDate(projectContext?.start_date);
  const winProbStr = fmtWinProb(projectContext?.win_probability);
  const metaLine = [closeDate ? `Close: ${closeDate}` : '', startDate ? `Start: ${startDate}` : '', winProbStr || ''].filter(Boolean).join('  ·  ');

  const overview = content.team_overview || {};
  const profiles = content.individual_profiles || [];
  const memberLine = profiles.map(p => `${escHtml(p.name)} (${escHtml(p.role)})`).join('  ·  ');
  const strengths = content.team_strengths || {};

  const profilesHtml = profiles.map(p =>
    `<div class="card card-accent" style="page-break-inside:avoid;margin-bottom:14px">
      <div style="display:flex;justify-content:space-between;align-items:baseline;margin-bottom:6px">
        <strong style="font-size:14px;color:#111">${escHtml(p.name)}</strong>
        <span style="font-size:12px;color:${BRAND_PURPLE};font-weight:600">${escHtml(p.role)}</span>
      </div>
      <p style="color:#333;margin-bottom:8px">${escHtml(p.profile)}</p>
      ${p.key_qualifications?.length ? bulletList(p.key_qualifications, BRAND_PURPLE_DARK, '✓') : ''}
    </div>`
  ).join('');

  const body = `
    <div class="page-header">
      <div>
        <div class="doc-label">Concierge · Client Introduction</div>
        <h1>${clientName ? `${escHtml(clientName)} — ` : ''}${escHtml(projectName) || 'Professional Team Introduction'}</h1>
        ${metaLine ? `<div class="doc-meta">${escHtml(metaLine)}</div>` : ''}
        ${memberLine ? `<div style="font-size:11px;color:#666;margin-top:3px">${memberLine}</div>` : ''}
      </div>
      <div class="doc-date">Generated ${escHtml(dateStr)}</div>
    </div>

    ${content.executive_summary ? `
      <div class="section">
        <h2>Executive Summary</h2>
        <div class="highlight-box">${escHtml(content.executive_summary)}</div>
      </div>` : ''}

    ${(overview.headline || overview.key_differentiators?.length || overview.success_metrics) ? `
      <div class="section">
        <h2>Team Overview</h2>
        ${overview.headline ? `<p style="font-size:15px;font-weight:600;color:#111;margin-bottom:10px">${escHtml(overview.headline)}</p>` : ''}
        ${(() => {
          const sm = overview.success_metrics || {};
          const stats = [
            sm.combined_experience,
            sm.client_satisfaction ?? sm.average_client_satisfaction,
            sm.capability_coverage ?? sm.technical_coverage,
          ].filter(Boolean);
          return stats.length ? `<div style="display:flex;gap:16px;flex-wrap:wrap;margin-bottom:12px">${
            stats.map(s => `<div style="flex:1;min-width:140px;padding:10px 14px;background:#F5F0FF;border-left:3px solid ${BRAND_PURPLE_DARK};border-radius:0 6px 6px 0"><p style="margin:0;font-size:12px;color:#333;line-height:1.5">${escHtml(s)}</p></div>`).join('')
          }</div>` : '';
        })()}
        ${overview.key_differentiators?.length ? bulletList(overview.key_differentiators, BRAND_PURPLE_DARK, '★') : ''}
      </div>` : ''}

    ${profiles.length ? `
      <div style="page-break-before:always">
        <h2>Team Profiles</h2>
        ${profilesHtml}
      </div>` : ''}

    ${(strengths.technical_excellence || strengths.collaboration_advantage || strengths.delivery_confidence) ? `
      <div class="section">
        <h2>Team Strengths</h2>
        ${strengths.technical_excellence ? `<div class="card" style="margin-bottom:10px"><strong style="color:#111">Technical Excellence</strong><p style="margin-top:6px">${escHtml(strengths.technical_excellence)}</p></div>` : ''}
        ${strengths.collaboration_advantage ? `<div class="card" style="margin-bottom:10px"><strong style="color:#111">Collaboration Advantage</strong><p style="margin-top:6px">${escHtml(strengths.collaboration_advantage)}</p></div>` : ''}
        ${strengths.delivery_confidence ? `<div class="card" style="margin-bottom:10px"><strong style="color:#111">Delivery Confidence</strong><p style="margin-top:6px">${escHtml(strengths.delivery_confidence)}</p></div>` : ''}
      </div>` : ''}

    ${content.success_factors?.length ? `
      <div class="section">
        <h2>Success Factors</h2>
        ${bulletList(content.success_factors, '#2E7D32', '●')}
      </div>` : ''}

    ${content.closing_statement ? `
      <div style="margin-top:24px;padding:16px 20px;background:${BRAND_PURPLE_LIGHT};border-radius:8px;border:1px solid #D9B3FF">
        <p style="font-style:italic;font-size:14px;color:#333;margin:0">${escHtml(content.closing_statement)}</p>
      </div>` : ''}

    <div class="footer">Generated by Concierge · ${escHtml(dateStr)} · Confidential</div>
  `;

  const title = `Team Introduction${projectName ? ` — ${projectName}` : ''}`;
  openPrintWindow(title, body);
}

// ─── Executive Summary ──────────────────────────────────────────────────────

export function generateExecutiveSummaryPDF(summary, configName, projectName, comparisonData, opportunityMeta = {}) {
  if (!summary) return;

  const dateStr = CURRENT_DATE.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  const docTitle = configName || projectName || 'Team Recommendation';
  const clientName = opportunityMeta.clientName || '';
  const closeDate = fmtDate(opportunityMeta.closeDate);
  const startDate = fmtDate(opportunityMeta.startDate);
  const winProbStr = fmtWinProb(opportunityMeta.winProbability);
  const metaLine = [closeDate ? `Close: ${closeDate}` : '', startDate ? `Start: ${startDate}` : '', winProbStr || ''].filter(Boolean).join('  ·  ');
  const rawMembers = opportunityMeta.teamMembers || [];
  const memberLine = rawMembers.map(m => `${escHtml(m.employee_name || m.name)} (${escHtml(m.role)})`).join('  ·  ');

  const fin = summary.financial_summary || {};
  const timeline = summary.timeline || {};

  // Teams Considered section — score grid per configuration
  const teamsSection = (() => {
    const configs = comparisonData?.configurations;
    if (!configs?.length) return '';
    const scoreNumColor = v => v == null ? '#888' : v >= 7 ? '#2E7D32' : v >= 5 ? '#E65100' : '#C62828';
    const scoreCell = (label, val, suffix = '') => val != null
      ? `<div style="text-align:center;min-width:52px;padding:4px 8px;background:#F5F5F5;border-radius:6px">
           <div style="font-size:10px;color:#888;text-transform:uppercase;letter-spacing:.3px;line-height:1.2">${label}</div>
           <div style="font-size:13px;font-weight:700;color:${scoreNumColor(val)}">${typeof val === 'number' ? val.toFixed(1) : val}${suffix}</div>
         </div>` : '';

    const configCards = configs.map(config => {
      const scores = config.team_analysis_data?.scores || {};
      const marginRaw = config.team_analysis_data?.financialView?.blendedMarginPct;
      const marginPct = marginRaw != null ? parseFloat(marginRaw) : (config.comparison_metrics?.margin_percentage ?? null);
      const isRecommended = config.configuration_name === configName;
      const members = config.team_members || [];
      return `
        <div class="card${isRecommended ? ' card-accent' : ''}" style="flex:1;min-width:180px">
          <div style="display:flex;align-items:center;gap:6px;margin-bottom:6px">
            ${isRecommended ? `<span style="font-size:10px;font-weight:700;color:#fff;background:#2E7D32;border-radius:4px;padding:1px 6px;letter-spacing:.3px">SELECTED</span>` : ''}
            <h3 style="margin:0;font-size:13px;color:${isRecommended ? '#2E7D32' : BRAND_PURPLE_DARK}">${escHtml(config.configuration_name)}</h3>
          </div>
          <div style="font-size:11px;color:#555;margin-bottom:8px;line-height:1.5">
            ${members.map(m => `<div>${escHtml(m.employee_name)} <span style="color:#888">— ${escHtml(m.role)}</span></div>`).join('')}
          </div>
          <div style="display:flex;flex-wrap:wrap;gap:5px">
            ${scoreCell('Overall', scores.overall)}
            ${scoreCell('Technical', scores.technical)}
            ${scoreCell('Chemistry', scores.chemistry)}
            ${scoreCell('Availability', scores.availability)}
            ${scoreCell('Experience', scores.experience)}
            ${marginPct != null ? scoreCell('Margin', marginPct, '%') : ''}
          </div>
        </div>`;
    }).join('');

    return `
      <div class="section">
        <h2>Teams Considered</h2>
        <div style="display:flex;flex-wrap:wrap;gap:12px">${configCards}</div>
      </div>`;
  })();

  const body = `
    <div class="page-header">
      <div>
        <div class="doc-label">Concierge · Executive Summary</div>
        <h1>${clientName ? `${escHtml(clientName)} — ` : ''}${escHtml(projectName || docTitle)}</h1>
        ${metaLine ? `<div class="doc-meta">${escHtml(metaLine)}</div>` : ''}
        ${memberLine ? `<div style="font-size:11px;color:#666;margin-top:3px">${memberLine}</div>` : ''}
      </div>
      <div class="doc-date">Generated ${escHtml(dateStr)}</div>
    </div>

    ${summary.recommendation ? `
      <div class="section">
        <h2>Executive Recommendation</h2>
        <div class="highlight-box">${escHtml(summary.recommendation)}</div>
      </div>` : ''}

    ${teamsSection}

    ${summary.team_overview?.length ? `
      <div class="section">
        <h2>Team Overview</h2>
        ${bulletList(summary.team_overview, BRAND_PURPLE_DARK, '→')}
      </div>` : ''}

    ${(fin.total_cost || fin.margin || fin.value_statement) ? `
      <div class="section">
        <h2>Financial Summary</h2>
        <div style="display:flex;gap:32px;margin-bottom:12px">
          ${fin.total_cost ? `<div><div style="font-size:11px;color:#888;text-transform:uppercase;letter-spacing:.5px">Estimated Cost</div><div style="font-size:18px;font-weight:700;color:#111">${escHtml(fin.total_cost)}</div></div>` : ''}
          ${fin.margin ? `<div><div style="font-size:11px;color:#888;text-transform:uppercase;letter-spacing:.5px">Margin</div><div style="font-size:18px;font-weight:700;color:#2E7D32">${escHtml(fin.margin)}</div></div>` : ''}
        </div>
        ${fin.value_statement ? `<p style="color:#333">${escHtml(fin.value_statement)}</p>` : ''}
      </div>` : ''}

    ${summary.success_factors?.length ? `
      <div class="section">
        <h2>Success Factors</h2>
        ${bulletList(summary.success_factors, '#2E7D32', '●')}
      </div>` : ''}

    ${(timeline.duration || timeline.readiness) ? `
      <div class="section">
        <h2>Project Timeline</h2>
        ${timeline.duration ? `<div style="margin-bottom:8px"><strong>Duration:</strong> ${escHtml(timeline.duration)}</div>` : ''}
        ${timeline.readiness ? `<p>${escHtml(timeline.readiness)}</p>` : ''}
      </div>` : ''}

    ${summary.considerations?.filter(c => c).length ? `
      <div class="section">
        <h2>Considerations</h2>
        ${bulletList(summary.considerations.filter(c => c), '#E65100', '▲')}
      </div>` : ''}

    <div class="footer">Generated by Concierge · ${escHtml(dateStr)} · Confidential — For internal use by resource management and consulting leadership</div>
  `;

  openPrintWindow(`${docTitle} — Executive Summary`, body);
}
